import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { googleAdsApi } from '@/services/api';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { CITY_MAP_OPTIONS, TONE } from './mapStyle';
import { CityDotsDrawer } from './CityDotsDrawer';
import {
  Globe2, Loader2, ChevronDown, ChevronRight, AlertTriangle, Wrench,
  ClipboardCheck, MousePointerClick,
} from 'lucide-react';

const num = (v) => Number(v || 0).toLocaleString('en-IN');

// A deliberate, fixed view of India rather than fitBounds: fitting the cities zooms into
// whichever corner has data, and fitting the country's full extent leaves India as a
// third of a map of Asia (the card is far wider than India is tall, in Mercator terms).
// This frames roughly 10°N-32°N / 64°E-95°E — Bengaluru to Delhi, with the country
// recognisable, which is the whole point of showing a map at all.
const INDIA_VIEW = { center: { lat: 20.6, lng: 79.0 }, zoom: 4.85 };
const INDIA_BOUNDS = { north: 35.6, south: 6.5, west: 68.0, east: 97.5 };

const Legend = ({ tone, label, count, testId }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-300" data-testid={testId}>
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: tone, boxShadow: `0 0 8px ${tone}` }} />
    {label}
    {count !== undefined && <span className="text-slate-400">· {num(count)}</span>}
  </span>
);

const LayerToggle = ({ on, onClick, tone, icon: Icon, label, count, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={on}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      on ? 'border-transparent text-slate-900' : 'border-slate-700 text-slate-400 hover:text-slate-200'
    }`}
    style={on ? { background: tone } : undefined}
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
    <span className={on ? 'text-slate-800/70' : 'text-slate-500'}>{num(count)}</span>
  </button>
);

export const DemandMapCard = ({ from, to }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layers, setLayers] = useState({ service: true, inspection: true });
  const [activeCity, setActiveCity] = useState(null);
  const [drawError, setDrawError] = useState(null);
  const [hover, setHover] = useState(null);

  const { ready, error: mapsError } = useGoogleMaps(open);
  const holder = useRef(null);
  const map = useRef(null);
  const overlays = useRef([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    googleAdsApi.getDemandMap({ date_from: from, date_to: to })
      .then((res) => { if (alive) { setData(res.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load the demand map'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to]);

  const cities = useMemo(() => {
    const rows = (data?.cities || []).map((c) => ({
      ...c,
      value: (layers.service ? c.service_leads : 0) + (layers.inspection ? c.inspections : 0),
    })).filter((c) => c.value > 0);
    const peak = Math.max(...rows.map((c) => c.value), 1);
    return rows.map((c) => ({ ...c, weight: c.value / peak })).sort((a, b) => b.value - a.value);
  }, [data, layers]);

  const clear = useCallback(() => {
    overlays.current.forEach((o) => o.setMap(null));
    overlays.current = [];
  }, []);

  // Draw: one soft halo + one solid core per city, so volume reads as a glow rather than
  // a pin. Radius scales on sqrt(weight) because a circle's AREA is what the eye compares.
  // Everything is inside a try/catch on purpose: a Google Maps failure must degrade to a
  // message inside this card, never throw into the page's error boundary and take the
  // whole Services tab down with it.
  useEffect(() => {
    if (!open || !ready || !holder.current || !cities.length) return undefined;
    try {
      const g = window.google.maps;
      if (!map.current) {
        map.current = new g.Map(holder.current, {
          ...CITY_MAP_OPTIONS,
          ...INDIA_VIEW,
          minZoom: 3.5,
          restriction: { latLngBounds: INDIA_BOUNDS, strictBounds: false },
        });
      }
      clear();
      setHover(null);

      cities.forEach((c) => {
        const tone = c.served ? TONE.served : TONE.unserved;
        const radius = 18000 + Math.sqrt(c.weight) * 90000;
        const position = { lat: c.lat, lng: c.lng };

        const halo = new g.Circle({
          map: map.current, center: position, radius: radius * 1.8, clickable: false,
          strokeWeight: 0, fillColor: tone.halo, fillOpacity: 0.13, zIndex: 1,
        });
        const core = new g.Circle({
          map: map.current, center: position, radius, clickable: true,
          strokeColor: tone.core, strokeOpacity: 0.9, strokeWeight: 1.5,
          fillColor: tone.core, fillOpacity: 0.42, zIndex: 2,
        });
        const label = new g.Marker({
          map: map.current, position, clickable: true, zIndex: 3,
          title: `${c.city} — click for customer locations`,
          icon: { path: g.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 },
          label: { text: `${c.city}  ${num(c.value)}`, color: '#e2e8f0',
                   fontSize: '11px', fontWeight: '600' },
        });

        const openCity = () => setActiveCity(c);
        const moveTip = (e) => {
          const rect = holder.current?.getBoundingClientRect();
          const dom = e?.domEvent;
          if (!rect || !dom) return;
          setHover({
            city: c,
            x: Math.min(Math.max(dom.clientX - rect.left, 8), rect.width - 200),
            y: Math.max(dom.clientY - rect.top - 12, 8),
          });
        };

        core.addListener('mouseover', (e) => {
          core.setOptions({ fillOpacity: 0.62, strokeWeight: 2.5 });
          moveTip(e);
        });
        core.addListener('mousemove', moveTip);
        label.addListener('mouseover', moveTip);
        core.addListener('mouseout', () => {
          core.setOptions({ fillOpacity: 0.42, strokeWeight: 1.5 });
          setHover(null);
        });
        core.addListener('click', openCity);
        label.addListener('click', openCity);

        overlays.current.push(halo, core, label);
      });

      setDrawError(null);
    } catch (e) {
      setDrawError(e.message || 'Could not draw the map');
    }
    return clear;
  }, [open, ready, cities, data, clear]);

  const t = data?.totals || {};
  const noCity = data?.no_city || {};
  const totalService = (data?.cities || []).reduce((s, c) => s + c.service_leads, 0);
  const totalInspections = (data?.cities || []).reduce((s, c) => s + c.inspections, 0);

  return (
    <div className="bg-white rounded-xl border mb-6 overflow-hidden" data-testid="demand-map-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="demand-map-toggle"
        className="w-full px-5 py-4 border-b bg-gradient-to-r from-slate-900 to-slate-800 flex items-start justify-between gap-4 text-left hover:from-slate-900 hover:to-slate-700 transition-colors"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 h-8 w-8 rounded-lg bg-white/10 text-sky-300 flex items-center justify-center shrink-0">
            <Globe2 className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-base font-semibold text-white">Demand on the map</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Every city that asked us for something, sized by volume — red where car
              service is not live yet. Click a city to see where its customers actually are.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-slate-400 hidden sm:inline">
            {num(t.cities)} cities · {num(t.unserved_cities)} not served
          </span>
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" />
                : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="bg-slate-900">
          {loading && !data && (
            <div className="p-6 flex items-center gap-2 text-sm text-slate-400" data-testid="demand-map-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Placing the demand on the map…
            </div>
          )}

          {error && (
            <div className="p-5 flex items-start gap-2 text-sm text-rose-300" data-testid="demand-map-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {(mapsError || drawError) && !error && (
            <div className="p-5 flex items-start gap-2 text-sm text-amber-300" data-testid="demand-map-maps-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              {mapsError || drawError}. The city list below still works.
            </div>
          )}

          {data && (
            <>
              <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-2">
                <LayerToggle on={layers.service} tone={TONE.service} icon={Wrench}
                             label="Service enquiries" count={totalService}
                             testId="demand-map-layer-service"
                             onClick={() => setLayers((l) => ({ ...l, service: !l.service }))} />
                <LayerToggle on={layers.inspection} tone={TONE.inspection} icon={ClipboardCheck}
                             label="Inspections" count={totalInspections}
                             testId="demand-map-layer-inspection"
                             onClick={() => setLayers((l) => ({ ...l, inspection: !l.inspection }))} />
                <span className="mx-1 h-4 w-px bg-slate-700 hidden sm:inline-block" />
                <Legend tone={TONE.served.core} label="Car service live" count={t.served_cities} testId="demand-map-legend-served" />
                <Legend tone={TONE.unserved.core} label="Not live yet" count={t.unserved_cities} testId="demand-map-legend-unserved" />
              </div>

              <div className="mx-5 mb-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                <div className="relative rounded-xl overflow-hidden border border-slate-700">
                  <div ref={holder} className="h-[520px] w-full bg-slate-900" data-testid="demand-map-canvas" />
                  {!ready && !mapsError && !drawError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 text-sm text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading Google Maps…
                    </div>
                  )}
                  {ready && !cities.length && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400"
                         data-testid="demand-map-empty">
                      Nothing to plot for this date range.
                    </div>
                  )}
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 text-[10px] text-slate-400 backdrop-blur">
                    <MousePointerClick className="h-3 w-3" /> Click a city for customer locations
                  </div>
                  {hover && (
                    // A React tooltip, not a Google InfoWindow: the InfoWindow renders a
                    // transparent wrapper (and a tail down to the anchor) over the bubble
                    // and silently eats the click that was meant for it.
                    <div
                      className="pointer-events-none absolute z-10 w-[192px] -translate-y-full rounded-lg border border-slate-700 bg-slate-900/95 px-3 py-2 shadow-xl backdrop-blur"
                      style={{ left: hover.x, top: hover.y }}
                      data-testid="demand-map-tooltip"
                    >
                      <div className="text-sm font-semibold text-white">{hover.city.city}</div>
                      <div className={`text-[11px] font-semibold ${hover.city.served ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {hover.city.served ? 'Car service is live here' : 'Car service NOT live here'}
                      </div>
                      <dl className="mt-1.5 space-y-0.5 text-[11px] text-slate-300">
                        <div className="flex justify-between gap-2"><dt>Service enquiries</dt><dd className="font-semibold text-white">{num(hover.city.service_leads)}</dd></div>
                        <div className="flex justify-between gap-2"><dt>Inspections</dt><dd className="font-semibold text-white">{num(hover.city.inspections)}</dd></div>
                        <div className="flex justify-between gap-2"><dt>Paying customers</dt><dd className="font-semibold text-white">{num(hover.city.payers)}</dd></div>
                      </dl>
                      <div className="mt-1.5 text-[10px] font-semibold text-sky-400">
                        Click to see customer locations
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-700/70 bg-slate-800/30 p-2 max-h-[520px] overflow-y-auto"
                     data-testid="demand-map-city-list">
                  {cities.map((c) => (
                    <button
                      key={c.city}
                      type="button"
                      onClick={() => setActiveCity(c)}
                      data-testid={`demand-map-city-${c.city}`}
                      className="group w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 shrink-0 rounded-full"
                              style={{ background: c.served ? TONE.served.core : TONE.unserved.core,
                                       boxShadow: `0 0 6px ${c.served ? TONE.served.core : TONE.unserved.core}` }} />
                        <span className="truncate text-sm text-slate-200">{c.city}</span>
                        {!c.served && (
                          <span className="shrink-0 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-300">
                            not live
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-slate-400 group-hover:text-slate-100">
                        {num(c.value)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="px-5 pb-5 text-[11px] leading-relaxed text-slate-500">
                {Boolean(noCity.service_leads) && (
                  <span data-testid="demand-map-nocity">
                    {num(noCity.service_leads)} service enquiries and {num(noCity.inspections)}{' '}
                    inspections carried no city at all, so they cannot be placed anywhere —
                    guessing a location for them would invent demand.{' '}
                  </span>
                )}
                {Boolean(data.unlocated?.length) && (
                  <span data-testid="demand-map-unlocated">
                    Google could not locate {data.unlocated.length} city name(s):{' '}
                    {data.unlocated.slice(0, 6).join(', ')}
                    {data.unlocated.length > 6 ? ` and ${data.unlocated.length - 6} more` : ''}.{' '}
                  </span>
                )}
                Suburbs fold into their metro, the same rule the table above uses.
              </div>
            </>
          )}
        </div>
      )}

      <CityDotsDrawer city={activeCity} from={from} to={to} onClose={() => setActiveCity(null)} />
    </div>
  );
};

export default DemandMapCard;
